// File: src/pages/Invoices/InvoiceForm.jsx
import React, { useState, useEffect, useContext } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Autocomplete, TextField, Dialog, DialogContent, DialogTitle } from "@mui/material";
import axios from "../../axios";
import invoiceApi from "../../api/invoiceApi";
import { useAuth } from "../../context/AuthContext";
import { PERMISSIONS } from "../../constants/permissions";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Checkbox } from "../../components/ui/checkbox";
import { Card, CardContent } from "../../components/ui/card";
import { SettingsContext } from "../../contexts/SettingsContext";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import CustomerForm from "../customerSuplier/CustomerForm";
import ImportInvoiceItemsDialog from "./ImportInvoiceItemsDialog";
import * as XLSX from "xlsx";

const initialFormState = {
  invoiceTypeId: 0,
  invoiceNumber: "",
  paidAmount: 0,
  issueDate: new Date().toISOString(),
  referenceDate: new Date().toISOString(),
  referenceNumber: "-1",
  customerId: 0,
  supplierId: 0,
  customerPrevBalance: 0,
  stockId: 0,
  toStockId: 0,
  salesmanId: null,
  paymentMethodId: 0,
  safeId: 0,
  bankId: 0,
  notes: "",
  discount: 0,
  invoiceStatus: 0,
  lines: [],
  journalEntryGlobalId: null, // ✅ Add this
};

export default function InvoiceForm({ viewMode = false }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const { id, typeId } = useParams();
  const [searchParams] = useSearchParams();
  const returnOf = searchParams.get("returnOf");
  const fromQuoteId = searchParams.get("fromQuote") || searchParams.get("Qtation");

  const [editingId, setEditingId] = useState(id ?? null);
  const mode = viewMode
    ? "view"
    : editingId
    ? "edit"
    : returnOf
    ? "return"
    : fromQuoteId
    ? "fromQuote"
    : "new";

  const initialType = returnOf ? 3 : fromQuoteId ? 1 : typeId ? parseInt(typeId, 10) : 0;

  const companyId = parseInt(localStorage.getItem("companyId"), 10);
  const branchId = parseInt(localStorage.getItem("branchId"), 10);
  const userId = parseInt(localStorage.getItem("userId"), 10);
  
  // Security Context
  const restrictedSafeId = localStorage.getItem("safeId") ? parseInt(localStorage.getItem("safeId"), 10) : null;
  const restrictedStockId = localStorage.getItem("stockId") ? parseInt(localStorage.getItem("stockId"), 10) : null;
  
  // Multi-context support
  const allowedSafeIds = localStorage.getItem("allowedSafeIds") ? JSON.parse(localStorage.getItem("allowedSafeIds")) : [];
  const allowedStockIds = localStorage.getItem("allowedStockIds") ? JSON.parse(localStorage.getItem("allowedStockIds")) : [];

  const [layout, setLayout] = useState({ header: "", items: "" });

  const getLabel = (fieldName) => {
    return t(`invoice.fields.${fieldName}`);
  };

  const [visibleFields, setVisibleFields] = useState({
    InvoiceNumber: true,
    IssueDate: true,
    CustomerId: true,
    SupplierId: false,
    Notes: true,
    ProductId: true,
    Description: true,
    Quantity: true,
    UnitPrice: true,
    Discount: true,
    TaxPercent: true,
    LineTotal: true,
  });

  const fieldLabels = {
    InvoiceNumber: t('invoice.fields.invoiceNumber'),
    IssueDate: t('invoice.fields.invoiceDate'),
    CustomerId: t('invoice.fields.customer'),
    SupplierId: t('invoice.fields.supplier'),
    Notes: t('invoice.fields.notes'),
    ReferenceNumber: t('invoice.fields.referenceNumber'),
    ReferenceDate: t('invoice.fields.referenceDate'),
    ProductId: t('invoice.fields.item'),
    Description: t('invoice.fields.description'),
    Unit: t('invoice.fields.unit') || 'الوحدة',
    Quantity: t('invoice.fields.quantity'),
    UnitPrice: t('invoice.fields.price'),
    Discount: t('invoice.fields.discount'),
    TaxPercent: t('invoice.fields.taxPercent'),
    LineTotal: t('invoice.fields.total'),
  };

  const [visibleItemFields, setVisibleItemFields] = useState({
    ProductId: true,
    Description: true,
    Unit: true,
    Quantity: true,
    UnitPrice: true,
    Discount: true,
    TaxPercent: true,
    LineTotal: true,
    Net: true,
  });

  const { decimalPlaces } = useContext(SettingsContext);
  const stepValue = 1 / 10 ** decimalPlaces;

  const [types, setTypes] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [methods, setMethods] = useState([]);
  const [safes, setSafes] = useState([]);
  const [banks, setBanks] = useState([]);
  const [stocks, setStocks] = useState([]);
  const [products, setProducts] = useState([]);
  const [creditMethodId, setCreditMethodId] = useState(null);

  const [priceIncludesVAT, setPriceIncludesVAT] = useState(false);

  const [settings, setSettings] = useState({
    isPriceIncludesVAT: false,
    autoApproveInvoices: false,
    autoPrintAfterSave: false,
    allowNegativeSales: false,
    pricingPolicy: "",
    decimalPlaces: 2,
  });

  const [form, setForm] = useState({
    ...initialFormState,
    invoiceTypeId: initialType,
  });

  const [productSearch, setProductSearch] = useState("");
  const [showApproveButton, setShowApproveButton] = useState(false);
  const [isApproved, setIsApproved] = useState(false);
  const [zatcaStatus, setZatcaStatus] = useState(null); // REPORTED, FAILED, CLEARED
  const [zatcaResponse, setZatcaResponse] = useState(null); // For details if needed
  
  const [openCustomerModal, setOpenCustomerModal] = useState(false);
  const [importOpen, setImportOpen] = useState(false);

  const handleImportSuccess = (importedItems) => {
    const newLines = importedItems.map(item => {
        const { product: p, qty, price } = item;
        const unitsList = Array.isArray(p.units) ? p.units : [];
        const bu = unitsList.find(u => u.isBaseUnit) || {};
        
        // Use imported price if available, otherwise calculate based on policy
        // Note: pickUnitPriceForDirection uses current form state (isInbound)
        const unitPrice = price !== null ? price : pickUnitPriceForDirection(bu, p);
        
        return {
            productId: p.id,
            productName: p?.arabicName ?? p?.nameAr ?? p?.name ?? "",
            description: p?.description ?? "",
            unitId: bu?.unitId || 0,
            unitName: bu?.unitNameAr || "",
            qty: parseFloat(qty).toFixed(decimalPlaces),
            price: parseFloat(unitPrice).toFixed(decimalPlaces),
            discount: parseFloat(0).toFixed(decimalPlaces),
            taxRate: parseFloat(p?.taxRate || 0).toFixed(decimalPlaces),
            unitsList,
        };
    });

    setForm(f => ({ ...f, lines: [...f.lines, ...newLines] }));
  };

  const handleCustomerSuccess = (newCustomer) => {
    setCustomers(prev => [...prev, newCustomer]);
    setForm(f => ({ ...f, customerId: newCustomer.id }));
    setOpenCustomerModal(false);
    toast.success("تم إضافة العميل بنجاح وتحديده");
  };

  // إدراج متعدد للأصناف
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkSearch, setBulkSearch] = useState("");
  // خريطة: productId -> كمية
  const [bulkSelected, setBulkSelected] = useState({});

  // قراءة الباركود وإدراج الصنف تلقائياً
  const [barcode, setBarcode] = useState("");
  const [barcodeLoading, setBarcodeLoading] = useState(false);

  async function insertProductByBarcode(code) {
    const raw = (code || "").trim();
    if (!raw) return;
    try {
      setBarcodeLoading(true);
      // ابحث أولاً على مستوى المنتج
      let p = products.find(pr => (pr.barcode || "").toString() === raw);
      let chosenUnit = null;
      if (!p) {
        // ابحث على مستوى الوحدات لكل منتج
        for (const pr of products) {
          const units = Array.isArray(pr.units) ? pr.units : [];
          const match = units.find(u => (u.barcode || "").toString() === raw);
          if (match) {
            p = pr;
            chosenUnit = match;
            break;
          }
        }
      }

      if (!p) {
        toast.error("❌ لم يتم العثور على صنف بهذا الباركود");
        return;
      }

      // التحقق من الرصيد عند المبيعات الصادرة
      if (isOutbound) {
        try {
          const response = await axios.get(
            `/StockMovements/stock-balance/${companyId}`,
            { params: { productId: p.id, branchId, stockId: form.stockId } }
          );
          const availableQty = parseFloat(response.data ?? 0);
          if (!settings.allowNegativeSales && availableQty <= 0) {
            toast.error(t('invoice.messages.quantityNotAvailable'));
            return;
          }
        } catch {
          if (!settings.allowNegativeSales) {
            toast.warning(t('invoice.messages.cannotVerifyQuantity'));
            return;
          }
        }
      }

      const unitsList = Array.isArray(p.units) ? p.units : [];
      const baseUnit = chosenUnit || unitsList.find(u => u.isBaseUnit) || {};
      const line = {
        productId: p.id,
        productName: p?.arabicName ?? p?.nameAr ?? p?.name ?? "",
        description: p?.description ?? "",
        unitId: baseUnit?.unitId || 0,
        unitName: baseUnit?.unitNameAr || "",
        qty: parseFloat(1).toFixed(decimalPlaces),
        price: pickUnitPriceForDirection(baseUnit, p),
        discount: parseFloat(0).toFixed(decimalPlaces),
        taxRate: parseFloat(p?.taxRate || 0).toFixed(decimalPlaces),
        unitsList,
      };

      setForm(f => ({ ...f, lines: [...f.lines, line] }));
      toast.success("✅ تم إدراج الصنف عبر الباركود");
    } finally {
      setBarcode("");
      setBarcodeLoading(false);
    }
  }

  function handleBarcodeKeyDown(e) {
    if (e.key === 'Enter') {
      e.preventDefault();
      insertProductByBarcode(barcode);
    }
  }

  useEffect(() => {
    if (!companyId) return;
    axios.get(`/InvoiceTypes?companyId=${companyId}`).then(r => setTypes(r.data));
    axios.get(`/Customer/by-company/${companyId}`).then(r => setCustomers(r.data));
    axios.get(`/Supplier/by-company/${companyId}`).then(r => setSuppliers(r.data));
    axios.get(`/PaymentMethod/by-company/${companyId}`).then(r => setMethods(r.data));
    axios.get(`/Safe/by-company/${companyId}`).then(r => setSafes(r.data));
    axios.get(`/Bank/by-company/${companyId}`).then(r => setBanks(r.data));
    axios.get(`/Stock/by-company/${companyId}`).then(r => setStocks(r.data));
    axios.get(`/Product/by-company/${companyId}`).then(r => setProducts(r.data));
  }, [companyId]);

  const handleCustomizeLayout = () => {
    Swal.fire({
      title: t('invoice.titles.customizeAppearance'),
      html: `
        <h3 style="text-align:right">${t('invoice.titles.invoiceHeader')}</h3>
        ${Object.keys(visibleFields)
          .map(
            (f) => `
              <label style="display:block;text-align:right;margin:4px 0">
                <input type="checkbox" id="field-${f}" ${visibleFields[f] ? "checked" : ""}/>
                ${fieldLabels[f] || f}
              </label>`
          )
          .join("")}
        <hr/>
        <h3 style="text-align:right">${t('invoice.titles.invoiceLines')}</h3>
        ${Object.keys(visibleItemFields)
          .map(
            (f) => `
              <label style="display:block;text-align:right;margin:4px 0">
                <input type="checkbox" id="item-${f}" ${visibleItemFields[f] ? "checked" : ""}/>
                ${fieldLabels[f] || f}
              </label>`
          )
          .join("")}
      `,
      confirmButtonText: t('common.save'),
      preConfirm: () => {
        const updatedHeader = { ...visibleFields };
        Object.keys(updatedHeader).forEach((f) => {
          updatedHeader[f] = document.getElementById(`field-${f}`).checked;
        });

        const updatedItems = { ...visibleItemFields };
        Object.keys(updatedItems).forEach((f) => {
          updatedItems[f] = document.getElementById(`item-${f}`).checked;
        });

        setVisibleFields(updatedHeader);
        setVisibleItemFields(updatedItems);

        handleSaveLayout(updatedHeader, updatedItems);
      },
    });
  };

  useEffect(() => {
    if (!companyId || !form.invoiceTypeId) return;

    axios
      .get(`/InvoiceLayout/${companyId}/${form.invoiceTypeId}`)
      .then((res) => {
        setLayout(res.data);
        const parser = new DOMParser();

        const headerXml = parser.parseFromString(res.data.headerLayoutXml, "text/xml");
        const headerFields = {};
        headerXml.querySelectorAll("Field").forEach((f) => {
          headerFields[f.getAttribute("name")] = f.getAttribute("visible") === "true";
        });
        setVisibleFields((prev) => ({ ...prev, ...headerFields }));

        const itemsXml = parser.parseFromString(res.data.itemsLayoutXml, "text/xml");
        const itemFields = {};
        itemsXml.querySelectorAll("Field").forEach((f) => {
          itemFields[f.getAttribute("name")] = f.getAttribute("visible") === "true";
        });
        setVisibleItemFields((prev) => ({ ...prev, ...itemFields }));
      })
      .catch(() => {
        console.log("لا يوجد مظهر محفوظ، سيتم استخدام الافتراضي");
      });
  }, [companyId, form.invoiceTypeId]);

  const toggleField = (name) => {
    setVisibleFields((prev) => ({ ...prev, [name]: !prev[name] }));
  };

  const handleSaveLayout = async (headerFields, itemFields) => {
    const headerXml = `
    <Layout>
      ${Object.entries(headerFields)
        .map(
          ([name, vis], i) =>
            `<Field name='${name}' label='${fieldLabels[name] || name}' visible='${vis}' order='${i + 1}' />`
        )
        .join("\n")}
    </Layout>`;

    const itemsXml = `
    <Layout>
      ${Object.entries(itemFields)
        .map(
          ([name, vis], i) =>
            `<Field name='${name}' label='${fieldLabels[name] || name}' visible='${vis}' order='${i + 1}' />`
        )
        .join("\n")}
    </Layout>`;

    try {
      await axios.post("/InvoiceLayout/save", {
        companyId,
        invoiceTypeId: form.invoiceTypeId,
        headerLayoutXml: headerXml,
        itemsLayoutXml: itemsXml,
        userName: localStorage.getItem("username") || "system",
      });
      toast.success(t('invoice.messages.appearanceSaved'));
    } catch (err) {
      toast.error(t('invoice.messages.appearanceSaveFailed'));
    }
  };

  const partyForType = (typeId) => {
    if ([1, 8, 3, 10, 5].includes(typeId)) return "customer";
    if ([2, 7, 4, 6,12,13].includes(typeId)) return "supplier";
    // 12 = مناقلة, 13 = تسوية -> No party required usually, or internal
    return null;
  };

  const currentType = types.find(t => t.invoiceTypeId === form.invoiceTypeId);
  const direction = currentType?.direction ?? currentType?.Direction;
  const isInbound = direction === 1;
  const isOutbound = direction === 2;

  const party = partyForType(form.invoiceTypeId);
  const isCustomerParty = party === "customer";
  const isSupplierParty = party === "supplier";

  useEffect(() => {
    const credit = methods.find(m => m.nameAr === "آجل")?.id ?? null;
    setCreditMethodId(credit);
  }, [methods]);

  useEffect(() => {
    const party = partyForType(form.invoiceTypeId);
    setForm(f => ({
      ...f,
      customerId: party === "customer" ? (f.customerId || 0) : 0,
      supplierId: party === "supplier" ? (f.supplierId || 0) : 0,
    }));
  }, [form.invoiceTypeId]);

  const fetchNextInvoiceNumber = async typeId => {
    if (!typeId || mode !== "new" && mode !== "fromQuote") return;
    try {
      const res = await invoiceApi.getNextNumber(companyId, branchId, typeId);
      setForm(f => ({
        ...f,
        invoiceNumber: res.data.toString(),
        issueDate: new Date().toLocaleDateString('en-CA'),
        referenceDate: new Date().toLocaleDateString('en-CA'),
      }));
    } catch (error) {
      console.error(t('invoice.messages.fetchNextNumberFailed'), error);
    }
  };

  useEffect(() => {
    fetchNextInvoiceNumber(form.invoiceTypeId);
  }, [form.invoiceTypeId]);

  useEffect(() => {
    if (!companyId || !form.invoiceTypeId || methods.length === 0) return;

    axios.get("/ProgramSettings/by-company-and-type", {
      params: { companyId, invoiceTypeId: form.invoiceTypeId },
    })
      .then(r => {
        const s = r.data;
        setSettings(s);

        setPriceIncludesVAT(mode === "new" ? !!s.isPriceIncludesVAT : false);

        if (mode === "new") {
          const cashId = methods.find(m => m.nameAr === "نقدي")?.id;
          
          // Determine Safe/Stock IDs: Priority to Restricted > Settings > Default
          const finalSafeId = restrictedSafeId || s.defaultSafeId || f.safeId;
          const finalStockId = restrictedStockId || s.defaultStockId || f.stockId;

          setForm(f => ({
            ...f,
            customerId: s.defaultCustomerId ?? f.customerId,
            paymentMethodId: s.defaultPaymentMethodId ?? f.paymentMethodId,
            safeId: (s.defaultPaymentMethodId ?? f.paymentMethodId) === cashId ? finalSafeId : 0,
            bankId: (s.defaultPaymentMethodId ?? f.paymentMethodId) !== cashId ? (s.defaultBankId ?? f.bankId) : 0,
            stockId: finalStockId,
          }));
        }
      })
      .catch(console.error);
  }, [companyId, form.invoiceTypeId, methods, mode, restrictedSafeId, restrictedStockId]);

  useEffect(() => {
    const sourceId =
      mode === "edit" ? editingId :
        mode === "return" ? returnOf :
          mode === "fromQuote" ? fromQuoteId :
            null;

    if (!sourceId) return;
    if (products.length === 0) return;

    invoiceApi.getById(sourceId)
      .then(res => {
        const inv = res.data;
        const rawItems = inv.items || inv.invoiceItems || inv.details || [];

        const lines = rawItems.map(it => {
          const pid = Number(it.productId);
          const p = products.find(pr => Number(pr.id) === pid);

          const unitsList = Array.isArray(p?.units) ? p.units : (it.units || []);
          const u = unitsList.find(x => Number(x.unitId) === Number(it.unitId))
            || unitsList.find(x => x.isBaseUnit)
            || {};

          const tax = p?.taxRate ?? it.taxPercent ?? 0;

          return {
            productId: pid,
            productName: p?.arabicName ?? p?.nameAr ?? it.description ?? "",
            description: it.description ?? "",
            unitId: u?.unitId || 0,
            unitName: u?.unitNameAr || "",
            qty: parseFloat(it.quantity || 1).toFixed(decimalPlaces),
            price: pickUnitPriceForDirection(u, p),
            discount: parseFloat(it.discount || 0).toFixed(decimalPlaces),
            taxRate: parseFloat(tax).toFixed(decimalPlaces),
            unitsList,
          };
        });

        setForm(f => ({
          ...f,
          invoiceTypeId: mode === "return" ? 3 : mode === "fromQuote" ? 1 : inv.invoiceType,
          invoiceNumber: mode === "fromQuote" ? "" : inv.invoiceNumber,
          issueDate: mode === "fromQuote" ? new Date().toLocaleDateString('en-CA') : inv.issueDate.slice(0, 16),
          referenceNumber: mode === "return" ? inv.invoiceNumber : (inv.referenceNumber ?? inv.invoiceNumber),
          referenceDate: (mode === "return" ? inv.issueDate : (inv.referenceDate || inv.issueDate)).slice(0, 16),
          customerId: inv.customerId || f.customerId,
          supplierId: mode === "fromQuote" ? 0 : (inv.supplierId || 0),
          customerPrevBalance: parseFloat(inv.customerPrevBalance || 0),
          stockId: inv.stockId || f.stockId,
          salesmanId: inv.salesmanId || f.salesmanId,
          notes: inv.notes || "",
          discount: parseFloat(inv.discountTotal || 0).toFixed(decimalPlaces),
          invoiceStatus: mode === "fromQuote" ? 0 : inv.invoiceStatus,
          paidAmount: mode === "fromQuote" ? 0 : parseFloat(inv.paidAmount || 0),
          journalEntryGlobalId: inv.journalEntryGlobalId, // ✅ Populate from API
          lines,
        }));

        if (mode === "fromQuote") {
          fetchNextInvoiceNumber(1);
        }

        setIsApproved(false);
        setShowApproveButton(mode === "edit" ? inv.invoiceStatus === 0 : false);
        setZatcaStatus(inv.zatcaStatus || (inv.isSentToZatca ? "REPORTED" : null));
      })
      .catch(console.error);
  }, [mode, editingId, returnOf, fromQuoteId, products, decimalPlaces]);

  function pickUnitPriceForDirection(u, p) {
    const sale = u?.saleprice ?? p?.saleprice ?? 0;
    const buy = getPurchasePrice(u, p);
    const base = isInbound ? buy : sale;
    return parseFloat(base || 0).toFixed(decimalPlaces);
  }

  function getPurchasePrice(u, p) {
    return (
      u?.purchprice ??
      u?.purchaseprice ??
      p?.purchprice ??
      p?.purchaseprice ??
      u?.saleprice ??
      p?.saleprice ??
      0
    );
  }

  const calculateTotals = () => {
    let subtotalVisible = 0, totalLineDiscounts = 0, totalTaxableAmount = 0, totalVATCalculated = 0;

    form.lines.forEach(ln => {
      const qty = parseFloat(ln.qty) || 0;
      const price = parseFloat(ln.price) || 0;
      const lineDisc = parseFloat(ln.discount) || 0;
      const taxRate = parseFloat(ln.taxRate) || 0;

      const grossLine = qty * price;
      const netLineAfterDisc = grossLine - lineDisc;

      subtotalVisible += grossLine;
      totalLineDiscounts += lineDisc;

      if (priceIncludesVAT) {
        const taxable = netLineAfterDisc / (1 + taxRate);
        const vat = netLineAfterDisc - taxable;
        totalTaxableAmount += taxable;
        totalVATCalculated += vat;
      } else {
        totalTaxableAmount += netLineAfterDisc;
        totalVATCalculated += netLineAfterDisc * taxRate;
      }
    });

    const docDiscount = parseFloat(form.discount) || 0;
    const finalTaxable = totalTaxableAmount - docDiscount;

    const net = priceIncludesVAT
      ? subtotalVisible - totalLineDiscounts - docDiscount
      : finalTaxable + totalVATCalculated;

    return { subtotal: subtotalVisible, totalLineDiscounts, totalDiscount: docDiscount, totalAfterDiscount: finalTaxable, totalVAT: totalVATCalculated, net };
  };

  const totals = calculateTotals();

  useEffect(() => {
    if (form.paymentMethodId !== creditMethodId) {
      setForm(f => ({
        ...f,
        paidAmount: parseFloat(totals.net.toFixed(decimalPlaces)),
      }));
    }
  }, [form.paymentMethodId, totals.net, decimalPlaces, creditMethodId]);

  function handleChange(e) {
    const { name, value } = e.target;
    let newValue = value;
    if (
      [
        "paymentMethodId",
        "safeId",
        "bankId",
        "customerId",
        "supplierId",
        "stockId",
        "toStockId",
        "salesmanId",
        "invoiceStatus",
        "paidAmount",
      ].includes(name)
    ) {
      newValue = +value;
    } else if (name === "discount") {
      newValue = parseFloat(value).toFixed(decimalPlaces);
    }
    setForm(f => ({ ...f, [name]: newValue }));
  }

  function handleAddLine() {
    setForm(f => ({
      ...f,
      lines: [
        ...f.lines,
        {
          productId: 0,
          productName: "",
          description: "",
          unitId: 0,
          unitName: "",
          qty: parseFloat(1).toFixed(decimalPlaces),
          price: parseFloat(0).toFixed(decimalPlaces),
          discount: parseFloat(0).toFixed(decimalPlaces),
          taxRate: parseFloat(0).toFixed(decimalPlaces),
          unitsList: [],
        },
      ],
    }));
  }

  function handleRemoveLine(i) {
    setForm(f => ({ ...f, lines: f.lines.filter((_, j) => j !== i) }));
  }

  // تبديل اختيار صنف للإدراج المتعدد
  function toggleBulkProduct(productId, checked) {
    setBulkSelected(prev => {
      const next = { ...prev };
      if (checked) {
        if (next[productId] == null) next[productId] = 1;
      } else {
        delete next[productId];
      }
      return next;
    });
  }

  // تحديث كمية صنف محدد في الإدراج المتعدد
  function setBulkProductQty(productId, qtyRaw) {
    const qty = parseFloat(qtyRaw) || 0;
    setBulkSelected(prev => ({ ...prev, [productId]: qty }));
  }

  // تنفيذ الإدراج المتعدد للأصناف المحددة
  function handleBulkInsert() {
    const selectedIds = Object.keys(bulkSelected).map(Number);
    if (selectedIds.length === 0) {
      toast.warning("اختر أصنافًا أولاً");
      return;
    }

    const newLines = selectedIds.map(pid => {
      const p = products.find(pr => Number(pr.id) === pid);
      if (!p) return null;
      const unitsList = Array.isArray(p.units) ? p.units : [];
      const bu = unitsList.find(u => u.isBaseUnit) || {};
      const qty = parseFloat(bulkSelected[pid] ?? 1) || 1;
      return {
        productId: pid,
        productName: p?.arabicName ?? p?.nameAr ?? p?.name ?? "",
        description: p?.description ?? "",
        unitId: bu?.unitId || 0,
        unitName: bu?.unitNameAr || "",
        qty: parseFloat(qty).toFixed(decimalPlaces),
        price: pickUnitPriceForDirection(bu, p),
        discount: parseFloat(0).toFixed(decimalPlaces),
        taxRate: parseFloat(p?.taxRate || 0).toFixed(decimalPlaces),
        unitsList,
      };
    }).filter(Boolean);

    setForm(f => ({ ...f, lines: [...f.lines, ...newLines] }));
    setBulkSelected({});
    setBulkSearch("");
    setBulkOpen(false);
    toast.success("✅ تم إدراج الأصناف المحددة");
  }

  async function handleLineChange(idx, field, raw) {
    const lines = [...form.lines];
    const ln = { ...lines[idx] };

    if (field === "productName") {
      ln.productName = raw;
      const p = products.find(x => x.arabicName === raw);
      if (!p) {
        lines[idx] = ln;
        setForm(f => ({ ...f, lines }));
        return;
      }

      if (isOutbound) {
        try {
          const response = await axios.get(
            `/StockMovements/stock-balance/${companyId}`,
            { params: { productId: p.id, branchId, stockId: form.stockId } }
          );
          const availableQty = parseFloat(response.data ?? 0);
          if (!settings.allowNegativeSales && availableQty <= 0) {
            toast.error(t('invoice.messages.quantityNotAvailable'));
            return;
          }
        } catch {
          if (!settings.allowNegativeSales) {
            toast.warning(t('invoice.messages.cannotVerifyQuantity'));
            return;
          }
        }
      }

      ln.productId = p.id;
      ln.taxRate = parseFloat(p.taxRate || 0).toFixed(decimalPlaces);
      ln.unitsList = p.units || [];
      const bu = ln.unitsList.find(u => u.isBaseUnit) || {};
      ln.unitId = bu.unitId || 0;
      ln.unitName = bu.unitNameAr || "";
      // Default description from product if available
      ln.description = p?.description ?? ln.description ?? "";

      ln.price = pickUnitPriceForDirection(bu, p, ln.taxRate);
      ln.discount = parseFloat(0).toFixed(decimalPlaces);
      ln.qty = parseFloat(1).toFixed(decimalPlaces);
    } else if (field === "unitId") {
      const u = ln.unitsList.find(x => x.unitId === +raw) || {};
      ln.unitId = u.unitId || 0;
      ln.unitName = u.unitNameAr || "";
      ln.price = pickUnitPriceForDirection(u, null, ln.taxRate);
    } else if (field === "qty") {
      // السماح بالقيمة كما هي (نص) للسماح بالكتابة الحرة
      ln.qty = raw;
    } else if (field === "price") {
      // السماح بالقيمة كما هي (نص) للسماح بالكتابة الحرة
      ln.price = raw;
    } else if (field === "discount") {
      // السماح بالقيمة كما هي (نص) للسماح بالكتابة الحرة
      ln.discount = raw;
    } else if (field === "taxRate") {
      ln.taxRate = parseFloat(raw).toFixed(decimalPlaces);
    } else if (field === "description") {
      ln.description = raw;
    }

    lines[idx] = ln;
    setForm(f => ({ ...f, lines }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (viewMode) return;

    if (isCustomerParty && (!form.customerId || form.customerId === 0)) {
      toast.error(t('invoice.messages.selectCustomerFirst'));
      return;
    }
    if (isSupplierParty && (!form.supplierId || form.supplierId === 0)) {
      toast.error(t('invoice.messages.selectSupplierFirst'));
      return;
    }

    const cashId = methods.find(m => m.nameAr === "نقدي")?.id;
    if (form.paymentMethodId === cashId && !form.safeId) {
      return alert(t('invoice.messages.selectSafe'));
    }

    const productIds = new Set();
    for (let ln of form.lines) {
      const qty = parseFloat(ln.qty);
      if (!ln.productId || isNaN(qty) || qty <= 0) {
        toast.error(`${t('invoice.messages.invalidQuantity')} "${ln.productName}"`);
        return;
      }
      if (productIds.has(ln.productId)) {
        toast.error(`${t('invoice.messages.duplicateItem')} "${ln.productName}"`);
        return;
      }
      productIds.add(ln.productId);
    }

    if (isOutbound && !settings.allowNegativeSales) {
      let hasError = false;
      for (let ln of form.lines) {
        const qty = parseFloat(ln.qty);
        try {
          const res = await axios.get(
            `/StockMovements/stock-balance/${companyId}`,
            { params: { productId: ln.productId, branchId, stockId: form.stockId } }
          );
          const availableQty = parseFloat(res.data ?? 0);
          if (qty > availableQty) {
            toast.error(`${t('invoice.messages.insufficientQuantity')} "${ln.productName}" (${availableQty})`);
            hasError = true;
          }
        } catch {
          toast.error(`${t('invoice.messages.cannotCheckBalance')} "${ln.productName}"`);
          hasError = true;
        }
      }
      if (hasError) return;
    }

    const payloadItems = form.lines.map(ln => {
      let unitPriceForAPI = parseFloat(ln.price);
      if (priceIncludesVAT) {
        unitPriceForAPI = unitPriceForAPI / (1 + parseFloat(ln.taxRate));
      }
      return {
        productId: ln.productId,
        unitId: ln.unitId,
        quantity: parseFloat(ln.qty),
        unitPrice: unitPriceForAPI,
        discount: parseFloat(ln.discount),
        taxPercent: parseFloat(ln.taxRate),
        description: ln.description ?? "",
      };
    });

    let invoiceStatusToSend = form.invoiceStatus;
    if (mode === "new" && settings.autoApproveInvoices) {
      invoiceStatusToSend = 1;
    }

    const payload = {
      companyId,
      branchId,
      userId: userId || 0, // ✅ Send userId
      invoiceTypeId: form.invoiceTypeId,
      invoiceNumber: form.invoiceNumber,
      issueDate: new Date(form.issueDate).toISOString(),
      referenceNumber: form.referenceNumber,
      referenceDate: new Date(form.referenceDate).toISOString(),
      customerId: isCustomerParty ? form.customerId : 0,
      supplierId: isSupplierParty ? form.supplierId : 0,
      customerPrevBalance: parseFloat(form.customerPrevBalance),
      stockId: form.stockId,
      toStockId: form.invoiceTypeId === 12 ? form.toStockId : null,
      salesmanId: form.salesmanId,
      paymentMethodId: form.paymentMethodId,
      safeId: form.safeId,
      bankId: form.bankId,
      notes: form.notes,
      discount: parseFloat(form.discount),
      invoiceStatus: invoiceStatusToSend,
      paidAmount: parseFloat(form.paidAmount),
      totals: {
        subtotal: totals.subtotal,
        totalAfterDiscount: totals.totalAfterDiscount,
        totalVAT: totals.totalVAT,
        net: totals.net,
      },
      items: payloadItems,
    };

    if (mode === "edit") {
      payload.InvGlobalID = editingId;
    }

    try {
      if (mode === "return") {
        payload.invoiceTypeId = 3;
        payload.referenceNumber = form.referenceNumber || form.invoiceNumber;
        payload.referenceDate = new Date().toISOString();
      }

      console.log("🚀 Sending payload:", payload, "mode:", mode);
      const res =
        mode === "edit"
          ? await invoiceApi.update(editingId, payload)
          : await invoiceApi.create(payload);

      const invGlobalId = mode === "edit" ? editingId : res.data.invGlobalID;

      if (settings.autoPrintAfterSave) {
        toast.success(t('invoice.messages.invoiceSaved'));
        // الطباعة المباشرة (فتح نافذة الطباعة تلقائياً)
        window.open(`/invoices/print/${invGlobalId}?auto=true`, "_blank");
        
        // إذا كان هناك اعتماد تلقائي، لا حاجة لفعله هنا لأنه تم إرسال الحالة في الـ payload
        // فقط نقوم بتصفير النموذج
        resetForm();
      } else {
        Swal.fire({
          title: t('invoice.messages.invoiceSaved'),
          showCancelButton: true,
          showDenyButton: true,
          cancelButtonText: t('invoice.buttons.new'),
          confirmButtonText: t('invoice.buttons.print'),
          denyButtonText: t('invoice.buttons.approveInvoice'),
        }).then(async (result) => {
          if (result.isConfirmed) {
            window.open(`/invoices/print/${invGlobalId}`, "_blank");
            // بعد الطباعة مباشرة افتح فاتورة جديدة
            resetForm();
          } else if (result.isDenied) {
            // اعتماد الفاتورة فورًا ثم افتح فاتورة جديدة
            await handleApprove(invGlobalId);
            resetForm();
          } else {
            // زر "جديد"
            resetForm();
          }
        });
      }
    } catch (err) {
      console.error("❌ Save error:", err);

      const serverError =
        err.response?.data?.errors ||
        err.response?.data ||
        err.message;
      toast.error(
        t('invoice.messages.saveError') + ": " + JSON.stringify(serverError)
      );
    }
  }

  // تطبيق الإعدادات الافتراضية من إعدادات البرنامج لنوع الفاتورة المحدد
  async function applyProgramDefaults(typeId) {
    if (!companyId || !typeId) return;
    try {
      const r = await axios.get("/ProgramSettings/by-company-and-type", {
        params: { companyId, invoiceTypeId: typeId },
      });
      const s = r.data;
      setSettings(s);

      const cashIdLocal = methods.find(m => m.nameAr === "نقدي")?.id;
      const defaultPM = s.defaultPaymentMethodId ?? 0;
      setPriceIncludesVAT(!!s.isPriceIncludesVAT);

      // Priority: Restricted Single > Settings > Default
      // New Logic:
      // If allowedSafeIds has items, check if default safe is in it. If not, pick first allowed.
      // If restrictedSafeId (legacy single) exists, it overrides everything.
      
      let finalSafeId = s.defaultSafeId || 0;
      if (restrictedSafeId) {
          finalSafeId = restrictedSafeId;
      } else if (allowedSafeIds.length > 0) {
          // If default is not allowed, pick first allowed
          if (!allowedSafeIds.includes(finalSafeId)) {
              finalSafeId = allowedSafeIds[0];
          }
      }

      let finalStockId = s.defaultStockId || 0;
      if (restrictedStockId) {
          finalStockId = restrictedStockId;
      } else if (allowedStockIds.length > 0) {
          if (!allowedStockIds.includes(finalStockId)) {
              finalStockId = allowedStockIds[0];
          }
      }

      setForm(f => ({
        ...f,
        customerId: s.defaultCustomerId ?? 0,
        paymentMethodId: defaultPM || 0,
        safeId: defaultPM === cashIdLocal ? finalSafeId : 0,
        bankId: defaultPM !== cashIdLocal ? (s.defaultBankId ?? 0) : 0,
        stockId: finalStockId,
      }));
    } catch (err) {
      console.error("Failed to apply program defaults", err);
    }
  }

  const handleApprove = async (idToApprove = editingId) => {
    try {
      await invoiceApi.approve(idToApprove);
      toast.success(t('invoice.messages.invoiceApproved'));
      setShowApproveButton(false);
      setIsApproved(true);
      
      // Update Zatca Status locally to reflect success immediately
      setZatcaStatus("REPORTED"); 
    } catch {
      toast.error(t('invoice.messages.approvalFailed'));
    }
  };

  const handleShowZatcaDetails = () => {
    // Here we could fetch details if not already loaded, 
    // or just show a message for now since we have the status.
    Swal.fire({
      title: 'حالة الربط مع الهيئة',
      text: zatcaStatus === "REPORTED" ? "تم إرسال الفاتورة بنجاح ✅" : "لم يتم الإرسال بعد أو يوجد خطأ",
      icon: zatcaStatus === "REPORTED" ? 'success' : 'info'
    });
  };

  function resetForm() {
    setEditingId(null);
    const newType = form.invoiceTypeId || initialType || 0;
    setForm({ ...initialFormState, invoiceTypeId: newType });
    fetchNextInvoiceNumber(newType);
    setShowApproveButton(false);
    setIsApproved(false);
    setZatcaStatus(null);
    applyProgramDefaults(newType);

    // Apply restricted values if present even without program defaults
    let forcedStockId = form.stockId;
    if (restrictedStockId) forcedStockId = restrictedStockId;
    else if (allowedStockIds.length > 0 && !allowedStockIds.includes(form.stockId)) forcedStockId = allowedStockIds[0];

    let forcedSafeId = form.safeId;
    if (restrictedSafeId) forcedSafeId = restrictedSafeId;
    else if (allowedSafeIds.length > 0 && !allowedSafeIds.includes(form.safeId)) forcedSafeId = allowedSafeIds[0];

    setForm(f => ({
        ...f,
        stockId: forcedStockId,
        safeId: forcedSafeId
    }));
    
    navigate(`/invoices/new/${newType}`, { replace: true });
  }

  // التنقل بين السجلات عبر API
  async function goToInvoice(action) {
    try {
      let res;

       if (action === 'last') {
        res = await invoiceApi.getLast(companyId, form.invoiceTypeId);

            } else if (action === 'previous') {
        if (id) {
          res = await invoiceApi.getPrevious(id);
        } else {
          // عند عدم وجود id في وضع فاتورة جديدة، اعتبر "السابق" هو الأخير
          res = await invoiceApi.getLast(companyId, form.invoiceTypeId);
        }
      } else if (action === 'next') {
        if (id) {
          res = await invoiceApi.getNext(id);
        } else {
          // عند عدم وجود id في وضع فاتورة جديدة، اعتبر "التالي" هو الأول
          res = await invoiceApi.getFirst(companyId, form.invoiceTypeId);
        }
      } else if (action === 'first') {
        res = await invoiceApi.getFirst(companyId, form.invoiceTypeId);

      }

      const inv = res?.data;
      const nextId = inv?.invGlobalID || inv?.id || inv?.InvGlobalID;
      if (nextId) {
        // تعبئة التفاصيل فورًا كما في وضع التعديل
        const rawItems = inv.items || inv.invoiceItems || inv.details || [];
        const lines = rawItems.map(it => {
          const pid = Number(it.productId);
          const p = products.find(pr => Number(pr.id) === pid);
          const unitsList = Array.isArray(p?.units) ? p.units : (it.units || []);
          const u = unitsList.find(x => Number(x.unitId) === Number(it.unitId))
            || unitsList.find(x => x.isBaseUnit)
            || {};
          const tax = p?.taxRate ?? it.taxPercent ?? 0;
          
          // ✅ تصحيح: استخدام السعر المخزن في الفاتورة بدلاً من سعر المنتج الحالي
          // ✅ تصحيح: مراعاة حالة "السعر شامل الضريبة" عند العرض
          let storedPrice = parseFloat(it.unitPrice || 0);
          if (priceIncludesVAT) {
              storedPrice = storedPrice * (1 + parseFloat(tax));
          }

          return {
            productId: pid,
            productName: p?.arabicName ?? p?.nameAr ?? it.description ?? "",
            description: it.description ?? "",
            unitId: u?.unitId || 0,
            unitName: u?.unitNameAr || "",
            qty: parseFloat(it.quantity || 1).toFixed(decimalPlaces),
            price: storedPrice.toFixed(decimalPlaces), // ✅ استخدام السعر المحسوب
            discount: parseFloat(it.discount || 0).toFixed(decimalPlaces),
            taxRate: parseFloat(tax).toFixed(decimalPlaces),
            unitsList,
          };
        });

        setForm(f => ({
          ...f,
          invoiceTypeId: inv.invoiceType,
          invoiceNumber: inv.invoiceNumber,
          issueDate: inv.issueDate?.slice(0, 16),
          referenceNumber: inv.referenceNumber ?? inv.invoiceNumber,
          referenceDate: (inv.referenceDate || inv.issueDate)?.slice(0, 16),
          customerId: inv.customerId || f.customerId,
          supplierId: inv.supplierId || 0,
          customerPrevBalance: parseFloat(inv.customerPrevBalance || 0),
          stockId: inv.stockId || f.stockId,
          salesmanId: inv.salesmanId || f.salesmanId,
          notes: inv.notes || "",
          discount: parseFloat(inv.discountTotal || 0).toFixed(decimalPlaces),
          invoiceStatus: inv.invoiceStatus,
          paidAmount: parseFloat(inv.paidAmount || 0),
          lines,
        }));

        setShowApproveButton(inv.invoiceStatus === 0);
        setIsApproved(false);

        setEditingId(nextId);
        navigate(`/invoices/edit/${nextId}`);
      } else {
        toast.error('لا توجد بيانات للتنقل');
      }
    } catch (err) {
      toast.error('حدث خطأ أثناء التنقل بين الفواتير');
    }
  }
  const getApprovePermission = (typeId) => {
    switch (typeId) {
      case 1: return PERMISSIONS.INVOICE_SALES_APPROVE;
      case 2: return PERMISSIONS.INVOICE_PURCHASE_APPROVE;
      case 3: return PERMISSIONS.INVOICE_SALES_RETURN_APPROVE;
      case 4: return PERMISSIONS.INVOICE_PURCHASE_RETURN_APPROVE;
      case 7: return PERMISSIONS.INVOICE_OPENING_STOCK_APPROVE;
      case 8: return PERMISSIONS.INVOICE_STOCK_IN_APPROVE;
      case 9: return PERMISSIONS.INVOICE_STOCK_OUT_APPROVE;
      case 12: return PERMISSIONS.STOCK_MOVE; // مناقلة
      case 13: return PERMISSIONS.STOCK_MOVE; // تسوية
      default: return null;
    }
  };

  const cashId = methods.find(m => m.nameAr === "نقدي")?.id;
  const isCash = form.paymentMethodId === cashId;
  const isCredit = form.paymentMethodId === creditMethodId;
  const isNonCash = !isCash && !isCredit;

  const handleExportExcel = () => {
    const headerData = [
      [t('invoice.fields.invoiceType'), types.find(t => t.invoiceTypeId === form.invoiceTypeId)?.name || ""],
      [t('invoice.fields.invoiceNumber'), form.invoiceNumber],
      [t('invoice.fields.invoiceDate'), form.issueDate ? form.issueDate.slice(0, 10) : ""],
      [t('invoice.fields.customer'), customers.find(c => c.id === form.customerId)?.name || ""],
      [t('invoice.fields.notes'), form.notes],
      [], 
    ];

    const tableHeader = [
      "#",
      t('invoice.fields.item') || "الصنف",
      t('invoice.fields.description') || "الوصف",
      t('invoice.fields.unit') || "الوحدة",
      t('invoice.fields.quantity') || "الكمية",
      t('invoice.fields.price') || "السعر",
      t('invoice.fields.discount') || "الخصم",
      t('invoice.fields.taxPercent') || "الضريبة %",
      t('invoice.fields.total') || "المجموع",
      t('invoice.fields.net') || "الصافي"
    ];

    const itemsData = form.lines.map((ln, i) => {
        const qty = parseFloat(ln.qty) || 0;
        const price = parseFloat(ln.price) || 0;
        const discount = parseFloat(ln.discount) || 0;
        const taxRate = parseFloat(ln.taxRate) || 0;

        let lineTotalBeforeDiscount = qty * price;
        let lineAfterDiscount = lineTotalBeforeDiscount - discount;
        
        let taxable = lineAfterDiscount;
        let vat = 0;

        if (priceIncludesVAT) {
            taxable = lineAfterDiscount / (1 + taxRate);
            vat = lineAfterDiscount - taxable;
        } else {
            vat = lineAfterDiscount * taxRate;
        }
        
        const net = priceIncludesVAT ? lineAfterDiscount : lineAfterDiscount + vat;

        return [
            i + 1,
            ln.productName,
            ln.description,
            ln.unitName,
            qty,
            price,
            discount,
            (taxRate * 100).toFixed(2) + "%",
            lineTotalBeforeDiscount.toFixed(decimalPlaces),
            net.toFixed(decimalPlaces)
        ];
    });

    const footerData = [
        [],
        ["", "", "", "", "", "", "", t('invoice.summary.subtotal') || "المجموع الفرعي", totals.subtotal.toFixed(decimalPlaces)],
        ["", "", "", "", "", "", "", t('invoice.summary.invoiceDiscount') || "خصم الفاتورة", form.discount],
        ["", "", "", "", "", "", "", t('invoice.summary.vat') || "الضريبة", totals.totalVAT.toFixed(decimalPlaces)],
        ["", "", "", "", "", "", "", t('invoice.summary.net') || "الصافي", totals.net.toFixed(decimalPlaces)]
    ];

    const wsData = [...headerData, tableHeader, ...itemsData, ...footerData];
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(wsData);

    ws['!cols'] = [
        { wch: 5 }, { wch: 30 }, { wch: 20 }, { wch: 10 }, { wch: 10 }, 
        { wch: 10 }, { wch: 10 }, { wch: 10 }, { wch: 15 }, { wch: 15 }
    ];

    XLSX.utils.book_append_sheet(wb, ws, "Invoice");
    XLSX.writeFile(wb, `Invoice_${form.invoiceNumber || 'New'}.xlsx`);
  };

  return (
    <Card dir="rtl" className="w-full max-w-full mx-auto mt-6 shadow-lg">
      <CardContent className="p-6">
        {/* Header - محسّن مع أزرار التنقل */}
        <div className="flex justify-between items-center bg-gradient-to-r from-blue-600 to-blue-700 text-white h-12 px-3 rounded-lg shadow-md mb-4">
  <div className="flex items-center gap-3">
    <h2 className="text-xl font-bold">
      {mode === "view"
        ? t('invoice.titles.viewInvoice')
        : mode === "edit"
        ? t('invoice.titles.editInvoice')
        : mode === "return"
        ? t('invoice.titles.returnInvoice')
        : t('invoice.titles.newInvoice')}
    </h2>

    {(mode === "edit" || mode === "return") && (
      <span className="opacity-90 text-sm font-semibold"># {editingId || returnOf}</span>
    )}
    
    {/* شارة حالة الزكاة */}
    {zatcaStatus === "REPORTED" && (
      <span className="bg-green-500 text-white text-xs px-2 py-1 rounded-full font-bold shadow-sm animate-pulse cursor-pointer" onClick={handleShowZatcaDetails}>
        ✅ تم الإرسال للهيئة
      </span>
    )}
    {zatcaStatus === "FAILED" && (
      <span className="bg-red-500 text-white text-xs px-2 py-1 rounded-full font-bold shadow-sm cursor-pointer" onClick={handleShowZatcaDetails}>
        ❌ فشل الإرسال
      </span>
    )}
  </div>
</div>


        <form onSubmit={handleSubmit} className="space-y-4">
          {/* السطر الأول: نوع الفاتورة، رقم الفاتورة، التاريخ، المستودع، الصندوق */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
            {/* نوع الفاتورة */}
            <div>
              <label className="block mb-1 font-bold text-gray-900 text-base">
                {t('invoice.fields.invoiceType')}
              </label>
              <Input
                readOnly
                value={types.find(t => t.invoiceTypeId === form.invoiceTypeId)?.name || ""}
                className="w-full bg-white py-3 px-3 text-base rounded-lg border-2 border-gray-300 font-bold"
              />
            </div>

            {/* رقم الفاتورة */}
            <div>
              <label className="block mb-1 font-bold text-gray-900 text-base">
                {t('invoice.fields.invoiceNumber')}
              </label>
              <Input
                readOnly
                value={form.invoiceNumber}
                className="w-full bg-white py-3 px-3 text-base rounded-lg border-2 border-gray-300 font-bold text-blue-600"
              />
            </div>

            {/* تاريخ الفاتورة */}
            <div>
              <label className="block mb-1 font-bold text-gray-900 text-base">
                {t('invoice.fields.invoiceDate')}
              </label>
              <Input
                readOnly={viewMode}
                type="date"
                name="issueDate"
                value={form.issueDate}
                onChange={handleChange}
                className="w-full bg-white py-3 px-3 text-base rounded-lg border-2 border-gray-300 font-bold"
              />
            </div>

            {/* المستودع */}
            <div>
              <label className="block mb-1 font-bold text-gray-900 text-base">
                {form.invoiceTypeId === 12 ? t('invoice.fields.fromWarehouse') || 'من مستودع' : t('invoice.fields.warehouse')}
              </label>
              <select
                name="stockId"
                value={form.stockId}
                onChange={handleChange}
                className="w-full border-2 rounded-lg px-3 py-3 text-base bg-white border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 font-bold"
                disabled={viewMode || !!restrictedStockId}
              >
                <option value={0}>{t('invoice.options.selectWarehouse')}</option>
                {stocks
                    .filter(s => {
                        if (restrictedStockId) return s.id === restrictedStockId;
                        if (allowedStockIds.length > 0) return allowedStockIds.includes(s.id);
                        return true;
                    })
                    .map(s => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
            </div>

            {/* المستودع المحول إليه (فقط في حالة التحويل المخزني) */}
            {form.invoiceTypeId === 12 && (
              <div>
                <label className="block mb-1 font-bold text-gray-900 text-base">
                  {t('invoice.fields.toWarehouse') || 'إلى مستودع'}
                </label>
                <select
                  name="toStockId"
                  value={form.toStockId}
                  onChange={handleChange}
                  className="w-full border-2 rounded-lg px-3 py-3 text-base bg-white border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 font-bold"
                  disabled={viewMode}
                >
                  <option value={0}>{t('invoice.options.selectToWarehouse') || 'اختر المستودع'}</option>
                  {stocks
                      .filter(s => s.id !== form.stockId) // منع اختيار نفس المستودع
                      .map(s => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
              </div>
            )}

            {/* الصندوق/البنك */}
            {isCash ? (
              <div>
                <label className="block mb-1 font-bold text-gray-900 text-base">
                  {t('invoice.fields.safe')}
                </label>
                <select
                  name="safeId"
                  value={form.safeId}
                  onChange={handleChange}
                  className="w-full border-2 rounded-lg px-3 py-3 text-base bg-white border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 font-bold"
                  disabled={viewMode || !!restrictedSafeId}
                >
                  <option value={0}>{t('invoice.options.selectSafe')}</option>
                  {safes
                    .filter(s => {
                        if (restrictedSafeId) return s.safeId === restrictedSafeId;
                        if (allowedSafeIds.length > 0) return allowedSafeIds.includes(s.safeId);
                        return true;
                    })
                    .map(s => (
                    <option key={s.safeId} value={s.safeId}>{s.name}</option>
                  ))}
                </select>
              </div>
            ) : isNonCash ? (
              <div>
                <label className="block mb-1 font-bold text-gray-900 text-base">
                  {t('invoice.fields.bank')}
                </label>
                <select
                  name="bankId"
                  value={form.bankId}
                  onChange={handleChange}
                  className="w-full border-2 rounded-lg px-3 py-3 text-base bg-white border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 font-bold"
                  disabled={viewMode}
                >
                  <option value={0}>{t('invoice.options.selectBank')}</option>
                  {banks.map(b => (
                    <option key={b.id} value={b.id}>{b.name}</option>
                  ))}
                </select>
              </div>
            ) : <div></div>}
            {/* طريقة الدفع */}
            <div>
              <label className="block mb-1 font-bold text-gray-900 text-base">
                {t('invoice.fields.paymentMethod')}
              </label>
              <select
                name="paymentMethodId"
                value={form.paymentMethodId}
                onChange={handleChange}
                className="w-full border-2 rounded-lg px-3 py-3 text-base bg-white border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 font-bold"
                disabled={viewMode}
              >
                <option value={0}>{t('invoice.options.selectPaymentMethod')}</option>
                {methods.map(m => (
                  <option key={m.id} value={m.id}>{m.nameAr}</option>
                ))}
              </select>
            </div>
             {/* حالة الفاتورة */}
            <div>
              <label className="block mb-1 font-bold text-gray-900 text-base">
                {t('invoice.fields.invoiceStatus')}
              </label>
              <select
                name="invoiceStatus"
                value={form.invoiceStatus}
                onChange={handleChange}
                className="w-full border-2 rounded-lg px-3 py-3 text-base bg-white border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 font-bold"
                disabled={viewMode}
              >
                <option value={0}>{t('invoice.options.draft')}</option>
                <option value={1}>{t('invoice.options.approved')}</option>
                <option value={2}>{t('invoice.options.paid')}</option>
              </select>
            </div>
            {/* العميل/المورد */}
            {isSupplierParty && (
              <div>
                <label className="block mb-1 font-bold text-gray-900 text-base">
                  {t('invoice.fields.supplier')}
                </label>
                <select
                  name="supplierId"
                  value={form.supplierId}
                  onChange={handleChange}
                  className="w-full border-2 rounded-lg px-3 py-3 text-base bg-white border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 font-bold"
                  disabled={viewMode}
                >
                  <option value={0}>{t('invoice.options.selectSupplier')}</option>
                  {suppliers.map(s => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
              </div>
            )}
            {isCustomerParty && (
              <>
                <div>
                  <label className="block mb-1 font-bold text-gray-900 text-base">
                    {t('invoice.fields.customer')}
                  </label>
                  <div className="flex gap-2">
                    <Autocomplete
                      options={customers}
                      getOptionLabel={(option) => option.name || ""}
                      value={customers.find(c => c.id === form.customerId) || null}
                      onChange={(event, newValue) => {
                        // محاكاة حدث التغيير ليتوافق مع handleChange
                        handleChange({ target: { name: 'customerId', value: newValue ? newValue.id : 0 } });
                      }}
                      disabled={viewMode}
                      className="w-full"
                      renderInput={(params) => (
                        <TextField 
                          {...params} 
                          placeholder={t('invoice.options.selectCustomer')} 
                          variant="outlined"
                          sx={{
                              "& .MuiOutlinedInput-root": {
                                  backgroundColor: "white",
                                  borderRadius: "0.5rem",
                                  fontWeight: "bold",
                                  paddingRight: "9px !important" // fix for RTL
                              }
                          }}
                        />
                      )}
                    />
                    {!viewMode && (
                      <Button 
                          type="button" 
                          variant="primary" 
                          onClick={() => setOpenCustomerModal(true)}
                          className="px-3"
                      >
                          ➕
                      </Button>
                    )}
                  </div>
                </div>

                {/* رصيد العميل السابق */}
                {form.customerId !== 0 && (
                  <div>
                    <label className="block mb-1 font-bold text-gray-900 text-base">
                      {t('invoice.fields.customerPrevBalance')}
                    </label>
                    <Input
                      readOnly
                      value={form.customerPrevBalance.toFixed(decimalPlaces)}
                      className="w-full bg-gray-100 py-3 px-3 text-base rounded-lg border-2 border-gray-300 font-bold text-red-600"
                    />
                  </div>
                )}
              </>
            )}
          </div>
          {/* السطر الثالث: الملاحظات (سطر كامل) */}
          <div>
            <label className="block mb-1 font-bold text-gray-900 text-base">
              {t('invoice.fields.notes')}
            </label>
            <Input
              name="notes"
              value={form.notes}
              onChange={handleChange}
              className="w-full bg-white py-3 px-3 text-base rounded-lg border-2 border-gray-300 font-bold"
              readOnly={viewMode}
              placeholder="اكتب ملاحظاتك هنا..."
            />
          </div>

          {/* حقول المرجع (إذا كانت inbound) */}
          {isInbound && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block mb-1 font-bold text-gray-900 text-base">
                  {t('invoice.fields.referenceNumber')}
                </label>
                <Input
                  name="referenceNumber"
                  value={form.referenceNumber}
                  onChange={handleChange}
                  className="w-full bg-white py-3 px-3 text-base rounded-lg border-2 border-gray-300 font-bold"
                  readOnly={viewMode}
                />
              </div>
              <div>
                <label className="block mb-1 font-bold text-gray-900 text-base">
                  {t('invoice.fields.referenceDate')}
                </label>
                <Input
                  name="referenceDate"
                  type="date"
                  value={form.referenceDate}
                  onChange={handleChange}
                  className="w-full bg-white py-3 px-3 text-base rounded-lg border-2 border-gray-300 font-bold"
                  readOnly={viewMode}
                />
              </div>
            </div>
          )}

          {/* أزرار إضافة الأصناف */}
          {!viewMode && (
            <div className="flex items-center gap-2">
              <Button type="button" variant="primary" size="md" onClick={handleAddLine}>
                ➕ {t('invoice.buttons.addItemLine')}
              </Button>
              <Button type="button" variant="secondary" size="md" onClick={() => setBulkOpen(true)}>
                📦 إدراج متعدد
              </Button>
              <Button type="button" variant="outline" size="md" onClick={() => setImportOpen(true)} className="bg-green-50 text-green-700 border-green-200 hover:bg-green-100">
                📥 استيراد Excel
              </Button>
              {/* حقل الباركود */}
              <Input
                placeholder={t('scanBarcode') || 'ادخل الباركود ثم اضغط Enter'}
                value={barcode}
                onChange={(e) => setBarcode(e.target.value)}
                onKeyDown={handleBarcodeKeyDown}
                onBlur={(e) => {
                  // إدراج عند الخروج إذا كانت قيمة موجودة
                  if (barcodeLoading) return;
                  const val = (e.target.value || '').trim();
                  if (val) insertProductByBarcode(val);
                }}
                disabled={barcodeLoading}
                autoFocus
                className="ml-auto w-96 md:w-[32rem] lg:w-[40rem] py-3 px-4 text-lg rounded-lg border-2 border-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          )}

          {/* جدول الأصناف - محسّن */}
          <div className="overflow-x-auto rounded-lg border-2 border-gray-300 shadow-sm">
            <table className="min-w-full table-auto border-collapse">
              <thead>
                <tr className="bg-gradient-to-r from-gray-700 to-gray-800 text-white">
                  <th className="px-4 py-4 text-center border-b-2 border-gray-300 font-bold text-base">#</th>
                  {visibleItemFields.ProductId && <th className="px-4 py-4 text-center border-b-2 border-gray-300 font-bold text-base">{t('invoice.fields.item') || 'الصنف'}</th>}
                  {visibleItemFields.Description && <th className="px-4 py-4 text-center border-b-2 border-gray-300 font-bold text-base">{t('invoice.fields.description') || 'الوصف'}</th>}
                  {visibleItemFields.Unit && <th className="px-4 py-4 text-center border-b-2 border-gray-300 font-bold text-base">{t('invoice.fields.unit') || 'الوحدة'}</th>}
                  {visibleItemFields.Quantity && <th className="px-4 py-4 text-center border-b-2 border-gray-300 font-bold text-base">{t('invoice.fields.quantity') || 'الكمية'}</th>}
                  {visibleItemFields.UnitPrice && <th className="px-4 py-4 text-center border-b-2 border-gray-300 font-bold text-base">{t('invoice.fields.price') || 'السعر'}</th>}
                  {visibleItemFields.LineTotal && <th className="px-4 py-4 text-center border-b-2 border-gray-300 font-bold text-base">{t('invoice.fields.total') || 'المجموع'}</th>}
                  {visibleItemFields.Discount && <th className="px-4 py-4 text-center border-b-2 border-gray-300 font-bold text-base">{t('invoice.fields.discount') || 'الخصم'}</th>}
                  {visibleItemFields.TaxPercent && <th className="px-4 py-4 text-center border-b-2 border-gray-300 font-bold text-base">{t('invoice.fields.taxPercent') || 'الضريبة %'}</th>}
                  {visibleItemFields.Net && <th className="px-4 py-4 text-center border-b-2 border-gray-300 font-bold text-base">{t('invoice.fields.net') || 'الصافي'}</th>}
                  {!viewMode && <th className="px-4 py-4 text-center border-b-2 border-gray-300 font-bold text-base">{t('invoice.fields.delete') || 'حذف'}</th>}
                </tr>
              </thead>

              <tbody>
                {form.lines.map((ln, i) => {
                  const qty = parseFloat(ln.qty);
                  const price = parseFloat(ln.price);
                  const discount = parseFloat(ln.discount);
                  const taxRate = parseFloat(ln.taxRate);

                  let lineTotalBeforeDiscountDisplay = qty * price;
                  let lineAfterLineDiscountDisplay = lineTotalBeforeDiscountDisplay - discount;

                  let taxableAmountPerLine = lineAfterLineDiscountDisplay;
                  let vatAmountPerLine = 0;

                  if (priceIncludesVAT) {
                    taxableAmountPerLine = lineAfterLineDiscountDisplay / (1 + taxRate);
                    vatAmountPerLine = lineAfterLineDiscountDisplay - taxableAmountPerLine;
                  } else {
                    vatAmountPerLine = lineAfterLineDiscountDisplay * taxRate;
                  }

                  const lineNet = priceIncludesVAT
                    ? lineAfterLineDiscountDisplay
                    : lineAfterLineDiscountDisplay + vatAmountPerLine;

                  return (
                    <tr key={i} className="hover:bg-blue-50 transition-colors">
                      <td className="px-4 py-4 border text-center align-middle font-bold text-base bg-gray-50">{i + 1}</td>

                      {visibleItemFields.ProductId && (
                        <td className="px-4 py-4 border align-middle">
                          <input
                            type="text"
                            className="w-full border-2 rounded-lg px-3 py-3 text-base font-bold bg-white border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder={t('invoice.placeholders.searchItem')}
                            value={form.lines[i].productName}
                            onChange={(e) => {
                              const value = e.target.value;
                              handleLineChange(i, "productName", value);
                              setProductSearch(value);
                            }}
                            list={`product-options-${i}`}
                            disabled={viewMode}
                          />
                          <datalist id={`product-options-${i}`}>
                            {products
                              .filter((p) =>
                                (p.arabicName || "").toLowerCase().includes((productSearch || "").toLowerCase())
                              )
                              .map((p) => (
                                <option key={p.id} value={p.arabicName} />
                              ))}
                          </datalist>
                        </td>
                      )}

                      {visibleItemFields.Description && (
                        <td className="px-4 py-4 border align-middle">
                          <input
                            type="text"
                            className="w-full border-2 rounded-lg px-3 py-3 text-base font-bold bg-white border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder={t('invoice.placeholders.description')}
                            value={ln.description ?? ''}
                            onChange={(e) => handleLineChange(i, "description", e.target.value)}
                            disabled={viewMode}
                          />
                        </td>
                      )}

                      {visibleItemFields.Unit && (
                        <td className="px-4 py-4 border text-center align-middle">
                          <select
                            value={ln.unitId}
                            onChange={(e) => handleLineChange(i, "unitId", e.target.value)}
                            className="w-full border-2 rounded-lg px-3 py-3 text-base font-bold bg-white border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            disabled={viewMode}
                          >
                            <option value={0}>—</option>
                            {ln.unitsList.map((u) => (
                              <option key={u.unitId} value={u.unitId}>
                                {u.unitNameAr}
                              </option>
                            ))}
                          </select>
                        </td>
                      )}

                      {visibleItemFields.Quantity && (
                        <td className="px-4 py-4 border text-center align-middle">
                          <input
                            type="text"
                            inputMode="decimal"
                            value={ln.qty}
                            onChange={(e) => {
                              const value = e.target.value;
                              // السماح بالأرقام والفاصلة والنقطة فقط
                              if (value === '' || /^[0-9]*\.?[0-9]*$/.test(value)) {
                                handleLineChange(i, "qty", value);
                              }
                            }}
                            onBlur={(e) => {
                              // عند الخروج من الحقل، تنسيق الرقم
                              const value = parseFloat(e.target.value) || 0;
                              handleLineChange(i, "qty", value.toFixed(decimalPlaces));
                            }}
                            onFocus={(e) => e.target.select()}
                            className="w-32 bg-white py-3 px-3 text-base rounded-lg border-2 border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 text-right font-bold"
                            disabled={viewMode}
                          />
                        </td>
                      )}

                      {visibleItemFields.UnitPrice && (
                        <td className="px-4 py-4 border text-right align-middle">
                          <input
                            type="text"
                            inputMode="decimal"
                            value={ln.price}
                            onChange={(e) => {
                              const value = e.target.value;
                              // السماح بالأرقام والفاصلة والنقطة فقط
                              if (value === '' || /^[0-9]*\.?[0-9]*$/.test(value)) {
                                handleLineChange(i, "price", value);
                              }
                            }}
                            onBlur={(e) => {
                              // عند الخروج من الحقل، تنسيق الرقم
                              const value = parseFloat(e.target.value) || 0;
                              handleLineChange(i, "price", value.toFixed(decimalPlaces));
                            }}
                            onFocus={(e) => e.target.select()}
                            className="w-36 bg-white py-3 px-3 text-base rounded-lg border-2 border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 text-right font-bold"
                            disabled={viewMode}
                          />
                        </td>
                      )}

                      {/* إزالة الأعمدة المكررة للكمية والسعر لتتطابق العناوين مع الخلايا */}

                      {visibleItemFields.LineTotal && (
                        <td className="px-4 py-4 border text-right font-bold align-middle text-base text-gray-800 bg-gray-50">
                          {lineTotalBeforeDiscountDisplay.toFixed(decimalPlaces)}
                        </td>
                      )}

                      {visibleItemFields.Discount && (
                        <td className="px-4 py-4 border text-right align-middle">
                          <input
                            type="text"
                            inputMode="decimal"
                            value={ln.discount}
                            onChange={(e) => {
                              const value = e.target.value;
                              // السماح بالأرقام والنقطة فقط
                              if (value === '' || /^[0-9]*\.?[0-9]*$/.test(value)) {
                                handleLineChange(i, "discount", value);
                              }
                            }}
                            onBlur={(e) => {
                              const value = parseFloat(e.target.value) || 0;
                              handleLineChange(i, "discount", value.toFixed(decimalPlaces));
                            }}
                            onFocus={(e) => e.target.select()}
                            className="w-32 bg-white py-3 px-3 text-base rounded-lg border-2 border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 text-right font-bold"
                            disabled={viewMode}
                          />
                        </td>
                      )}

                      {visibleItemFields.TaxPercent && (
                        <td className="px-4 py-4 border text-center align-middle">
                          <div className="flex items-center justify-end">
                            <Input
                              type="number"
                              step={stepValue}
                              value={(parseFloat(ln.taxRate) * 100).toFixed(decimalPlaces)}
                              onChange={(e) =>
                                handleLineChange(i, "taxRate", parseFloat(e.target.value) / 100)
                              }
                              className="w-24 bg-white py-3 px-2 text-base rounded-lg border-2 border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 text-right font-bold inline-block"
                              readOnly={viewMode}
                            />
                            <span className="ml-1 font-bold text-base">%</span>
                          </div>
                        </td>
                      )}

                      {visibleItemFields.Net && (
                        <td className="px-4 py-4 border text-right font-bold align-middle text-lg text-blue-600 bg-blue-50">
                          {lineNet.toFixed(decimalPlaces)}
                        </td>
                      )}

                      {!viewMode && (
                        <td className="px-4 py-4 border text-center align-middle">
                          <Button
                            variant="outline"
                            size="icon"
                            className="text-lg p-3 bg-red-100 hover:bg-red-200 text-red-600 rounded-lg font-bold"
                            onClick={() => handleRemoveLine(i)}
                          >
                            🗑️
                          </Button>
                        </td>
                      )}
                    </tr>
                  );
                })}
                {form.lines.length === 0 && (
                  <tr>
                    <td
                      colSpan={
                        1 // رقم الصف
                        + (visibleItemFields.ProductId ? 1 : 0)
                        + (visibleItemFields.Description ? 1 : 0)
                        + (visibleItemFields.Unit ? 1 : 0)
                        + (visibleItemFields.Quantity ? 1 : 0)
                        + (visibleItemFields.UnitPrice ? 1 : 0)
                        + (visibleItemFields.LineTotal ? 1 : 0)
                        + (visibleItemFields.Discount ? 1 : 0)
                        + (visibleItemFields.TaxPercent ? 1 : 0)
                        + (visibleItemFields.Net ? 1 : 0)
                        + (!viewMode ? 1 : 0) // عمود الحذف عند التحرير
                      }
                      className="py-12 text-center text-gray-500 text-lg font-bold"
                    >
                      {t('invoice.messages.noLinesYet')}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* نافذة الإدراج المتعدد للأصناف */}
          {bulkOpen && !viewMode && (
            <div className="fixed inset-0 z-50 flex items-center justify-center">
              <div className="absolute inset-0 bg-black/40" onClick={() => setBulkOpen(false)} />
              <div className="relative bg-white w-full max-w-3xl rounded-lg shadow-xl border-2 border-gray-300 p-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-bold text-lg">إدراج متعدد للأصناف</h3>
                  <Button variant="outline" onClick={() => setBulkOpen(false)}>إغلاق</Button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
                  <div className="md:col-span-2">
                    <Input
                      placeholder="بحث عن صنف بالاسم"
                      value={bulkSearch}
                      onChange={(e) => setBulkSearch(e.target.value)}
                      className="w-full"
                    />
                  </div>
                  <div className="md:col-span-1 text-right">
                    <Button variant="primary" onClick={handleBulkInsert}>إدراج الأصناف المحددة</Button>
                  </div>
                </div>

                <div className="max-h-80 overflow-y-auto border rounded">
                  <table className="min-w-full table-auto">
                    <thead>
                      <tr className="bg-gray-100">
                        <th className="px-3 py-2 text-center">اختيار</th>
                        <th className="px-3 py-2 text-center">الصنف</th>
                        <th className="px-3 py-2 text-center">الوحدة الأساسية</th>
                        <th className="px-3 py-2 text-center">الكمية</th>
                      </tr>
                    </thead>
                    <tbody>
                      {products
                        .filter(p => (p.arabicName || "").toLowerCase().includes((bulkSearch || "").toLowerCase()))
                        .map(p => {
                          const unitsList = Array.isArray(p.units) ? p.units : [];
                          const bu = unitsList.find(u => u.isBaseUnit) || {};
                          const qtyVal = bulkSelected[p.id] ?? 0;
                          const checked = qtyVal > 0 || Object.prototype.hasOwnProperty.call(bulkSelected, p.id);
                          return (
                            <tr key={p.id} className="hover:bg-gray-50">
                              <td className="px-3 py-2 text-center">
                                <Checkbox
                                  checked={!!checked}
                                  onChange={(e) => toggleBulkProduct(p.id, e.target.checked)}
                                />
                              </td>
                              <td className="px-3 py-2 text-right font-bold">{p.arabicName}</td>
                              <td className="px-3 py-2 text-center">{bu?.unitNameAr || ""}</td>
                              <td className="px-3 py-2 text-center">
                                <Input
                                  type="number"
                                  min="0"
                                  step="1"
                                  value={qtyVal || 1}
                                  onChange={(e) => setBulkProductQty(p.id, e.target.value)}
                                  className="w-24 text-right"
                                />
                              </td>
                            </tr>
                          );
                        })}
                    </tbody>
                  </table>
                </div>

                <div className="mt-3 flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setBulkOpen(false)}>إلغاء</Button>
                  <Button variant="primary" onClick={handleBulkInsert}>إدراج</Button>
                </div>
              </div>
            </div>
          )}

          {/* ملخص الفاتورة - محسّن */}
          <div className="mt-6 p-6 bg-gradient-to-br from-gray-50 to-gray-100 border-2 border-gray-300 rounded-lg shadow-md">
            <div className="grid grid-cols-1 gap-3 max-w-md ml-auto">
              <div className="flex justify-between items-center p-2 border-b border-gray-300">
                <span className="text-right font-bold text-gray-900 text-base">{t('invoice.summary.subtotal')}:</span>
                <span className="text-left font-bold text-gray-900 text-lg">
                  {totals.subtotal.toFixed(decimalPlaces)}
                </span>
              </div>

              <div className="flex justify-between items-center p-2 border-b border-gray-300">
                <span className="text-right font-bold text-gray-900 text-base">{t('invoice.summary.invoiceDiscount')}:</span>
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    step={stepValue}
                    name="discount"
                    value={form.discount}
                    onChange={handleChange}
                    className="w-32 bg-white py-2 px-3 text-base rounded-lg border-2 border-gray-300 text-right font-bold"
                    readOnly={viewMode}
                  />
                </div>
              </div>

              <div className="flex justify-between items-center p-2 border-b border-gray-300">
                <span className="text-right font-bold text-gray-900 text-base">{t('invoice.summary.afterDiscount')}:</span>
                <span className="text-left font-bold text-gray-900 text-lg">
                  {totals.totalAfterDiscount.toFixed(decimalPlaces)}
                </span>
              </div>

              <div className="flex justify-between items-center p-2 border-b border-gray-300">
                <span className="text-right font-bold text-gray-900 text-base">{t('invoice.summary.vat')}:</span>
                <span className="text-left font-bold text-gray-900 text-lg">
                  {totals.totalVAT.toFixed(decimalPlaces)}
                </span>
              </div>

              <div className="flex justify-between items-center bg-gradient-to-r from-blue-600 to-blue-700 text-white p-4 rounded-lg shadow-lg mt-2">
                <strong className="text-lg font-bold">{t('invoice.summary.net')}:</strong>
                <span className="text-2xl font-extrabold">
                  {totals.net.toFixed(decimalPlaces)}
                </span>
              </div>
            </div>
          </div>

          {/* الأزرار - محسّنة */}
          <div className="flex flex-col xl:flex-row justify-between items-center gap-4 mt-6">
            {/* أزرار الإجراءات الرئيسية + التنقل في جهة اليمين (بداية السطر RTL) */}
            <div className="flex flex-wrap items-center gap-3 w-full xl:w-auto justify-center xl:justify-start">
              {!viewMode && (
                <Button type="button" variant="secondary" size="md" onClick={resetForm}>
                  🧾 {t('invoice.buttons.new')}
                </Button>
              )}
              {!viewMode && (
                <Button type="submit" variant="success" size="lg">
                  💾 {t('invoice.buttons.saveInvoice')}
                </Button>
              )}
              {id && (
                <Button type="button" variant="secondary" size="md" onClick={() => window.open(`/invoices/print/${id}`, "_blank")}>
                  🖨️ طباعة
                </Button>
              )}
              
              <Button type="button" variant="outline" size="md" onClick={handleExportExcel} className="bg-green-50 text-green-700 border-green-200 hover:bg-green-100">
                📊 تصدير Excel
              </Button>

              {/* ✅ View Journal Entry Button */}
              {(form.journalEntryGlobalId) && (
                <Button 
                  type="button" 
                  variant="outline" 
                  size="md" 
                  className="bg-purple-100 text-purple-700 hover:bg-purple-200 border-purple-300"
                  onClick={() => window.open(`/journal-entries/${form.journalEntryGlobalId}/view`, "_blank")}
                >
                  👁️ عرض القيد
                </Button>
              )}

              {editingId && showApproveButton && !isApproved && hasPermission(getApprovePermission(form.invoiceTypeId)) && (
                <Button type="button" variant="success" size="lg" onClick={handleApprove}>
                  ✅ {t('invoice.buttons.approveInvoice')}
                </Button>
              )}

              {/* أزرار التنقل بين الفواتير — نفس السطر ودائمًا */}
              <div className="flex items-center gap-2 mt-2 xl:mt-0">
                                <Button type="button" className="bg-gray-600 hover:bg-gray-700 text-white px-3 py-2 rounded-lg font-bold text-sm shadow-md" onClick={() => goToInvoice('last')}>⏭️</Button>
                <Button type="button" className="bg-gray-600 hover:bg-gray-700 text-white px-3 py-2 rounded-lg font-bold text-sm shadow-md" onClick={() => goToInvoice('next')}>▶️</Button>
                <Button type="button" className="bg-gray-600 hover:bg-gray-700 text-white px-3 py-2 rounded-lg font-bold text-sm shadow-md" onClick={() => goToInvoice('previous')}>◀️</Button>
                <Button type="button" className="bg-gray-600 hover:bg-gray-700 text-white px-3 py-2 rounded-lg font-bold text-sm shadow-md" onClick={() => goToInvoice('first')}>⏮️</Button>

              </div>
            </div>

            {/* زر تخصيص المظهر في أقصى اليسار (نهاية السطر RTL) */}
            <div className="flex items-center gap-2 w-full xl:w-auto justify-center xl:justify-end">
              <Button type="button" variant="outline" size="md" onClick={handleCustomizeLayout}>
                ⚙️ {t('invoice.buttons.customizeAppearance')}
              </Button>
            </div>
          </div>
        </form>

        <Dialog open={openCustomerModal} onClose={() => setOpenCustomerModal(false)} dir="rtl" maxWidth="sm" fullWidth>
            <DialogTitle>إضافة عميل جديد</DialogTitle>
            <DialogContent>
                <CustomerForm 
                    isModal={true} 
                    onSuccess={handleCustomerSuccess} 
                    onCancel={() => setOpenCustomerModal(false)} 
                />
            </DialogContent>
        </Dialog>

        <ImportInvoiceItemsDialog 
          open={importOpen} 
          onClose={() => setImportOpen(false)} 
          onImport={handleImportSuccess}
          products={products}
        />

      </CardContent>
    </Card>
  );
}
